import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
    const context = useContext(AdminAuthContext);
    if (!context) {
        throw new Error('useAdminAuth must be used within AdminAuthProvider');
    }
    return context;
};

export const AdminAuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [accessKey, setAccessKey] = useState('');
    const navigate = useNavigate();

    // Check if we're in development mode
    const isDev = import.meta.env.DEV;

    useEffect(() => {
        // If not in dev mode, redirect to home
        if (!isDev) {
            navigate('/');
            return;
        }

        // Check for stored access key
        const storedKey = localStorage.getItem('admin_access_key');
        if (storedKey === 'safecare_admin_2026') {
            setIsAuthenticated(true);
        }
    }, [isDev, navigate]);

    const handleAccessKeySubmit = (key) => {
        if (key === 'safecare_admin_2026') {
            localStorage.setItem('admin_access_key', key);
            setIsAuthenticated(true);
            return true;
        }
        return false;
    };

    const logout = () => {
        localStorage.removeItem('admin_access_key');
        setIsAuthenticated(false);
        navigate('/');
    };

    if (!isDev) {
        return null;
    }

    if (!isAuthenticated) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
            }}>
                <div style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '16px',
                    padding: '48px',
                    maxWidth: '400px',
                    width: '100%'
                }}>
                    <h1 style={{
                        color: '#06b6d4',
                        marginBottom: '12px',
                        fontSize: '32px',
                        fontWeight: '700'
                    }}>
                        SAFECARE Admin
                    </h1>
                    <p style={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        marginBottom: '32px',
                        fontSize: '14px'
                    }}>
                        Development environment only
                    </p>
                    <form onSubmit={(e) => {
                        e.preventDefault();
                        const success = handleAccessKeySubmit(accessKey);
                        if (!success) {
                            alert('Invalid access key');
                            setAccessKey('');
                        }
                    }}>
                        <input
                            type="password"
                            value={accessKey}
                            onChange={(e) => setAccessKey(e.target.value)}
                            placeholder="Enter access key"
                            style={{
                                width: '100%',
                                padding: '12px 16px',
                                background: 'rgba(255, 255, 255, 0.05)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '8px',
                                color: '#fff',
                                fontSize: '16px',
                                marginBottom: '16px',
                                outline: 'none'
                            }}
                            autoFocus
                        />
                        <button
                            type="submit"
                            style={{
                                width: '100%',
                                padding: '12px 16px',
                                background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                                border: 'none',
                                borderRadius: '8px',
                                color: '#fff',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'transform 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                        >
                            Access Admin
                        </button>
                    </form>
                    <p style={{
                        color: 'rgba(255, 255, 255, 0.4)',
                        marginTop: '24px',
                        fontSize: '12px',
                        textAlign: 'center'
                    }}>
                        Access key: <code style={{ color: '#06b6d4' }}>safecare_admin_2026</code>
                    </p>
                </div>
            </div>
        );
    }

    return (
        <AdminAuthContext.Provider value={{ isAuthenticated, logout }}>
            {children}
        </AdminAuthContext.Provider>
    );
};
